import useApi from "@/hooks/useApi";
import useNetSuiteConfigs from "@/hooks/useNetSuiteConfigs";
import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from "react";
import Select, { SingleValue } from "react-select";
import Option from "react-select/dist/declarations/src/components/Option";
import NetSuiteModal from "./NetSuiteModal";
import LoadingSpinner from "@/components/LoadingSpinner";
import NetSuiteInfoMessage from "./NetSuiteInfoMessage";
import NetSuitePanelRow from "./NetSuitePanelRow";
import { ChevronDown, HelpCircle } from "@/components/ds/icons";
import { SecondaryButton, PrimaryButton, GhostButton } from "@/components/ds";
import CustomSelect from "./CustomSelect";
import { getMetaDataOptions } from "./MetaDataOptions";
import { Modal, ModalBody } from "@velaro/velaro-component-lib";
import { AlertTriangle, IconClose } from "@velaro/velaro-component-lib";
import { getRequiredFields } from "./NetsuiteRequiredFields";

// Column header help — always-accessible replacement for hover-only `title=`
// tooltips. The icon's accessible name (aria-label) is exposed to assistive
// tech and keyboard focus regardless of hover/touch support; `title` is kept
// as a bonus desktop hover hint, never as the sole explanation.
function ColumnHelp({ description }: { description: string }) {
  const [open, setOpen] = useState(false);
  const tooltipId = useId();
  return (
    <span className="relative inline-flex align-middle ml-1">
      <button
        type="button"
        tabIndex={0}
        aria-label={description}
        aria-expanded={open}
        aria-describedby={open ? tooltipId : undefined}
        onClick={() => setOpen(true)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex text-zinc-600 hover:text-zinc-800 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary/40 rounded-full"
      >
        <HelpCircle className="w-3.5 h-3.5" />
      </button>
      {open && (
        <span
          id={tooltipId}
          role="tooltip"
          className="absolute z-20 top-full left-1/2 -translate-x-1/2 mt-1 w-48 rounded-lg bg-zinc-800 text-white text-[11px] leading-snug px-2.5 py-1.5 shadow-lg normal-case"
        >
          {description}
        </span>
      )}
    </span>
  );
}

export interface NetSuiteMap {
  id: number;
  netSuiteField: SingleValue<Option>;
  label: string;
  linkedTo: string;
  create: boolean;
  usedForSearching: boolean;
  showWhenSelected: boolean;
  showInSearch: boolean;
  control: string;
  defaultValue: string;
  recordType: string;
  isRequired?: boolean;
}

interface Option {
  value: any;
  label: string;
  key?: string;
  disabled?: boolean;
}

interface NetSuiteConfig {
  id: number;
  displayName: string;
  company: string;
  teamId: number;
}

interface Props {
  teamId: number;
  metadataType: string;
  showSavedMessage: () => void;
  isCreateMode?: boolean;
  selectedConfigId: number;
  isDeleteDisabled: boolean;
  /** Optional live filter — only rows whose field name matches are shown. */
  filterText?: string;
  /** Called when a new row is added — clears any active search filter so the new (blank) row isn't immediately hidden. */
  onClearFilter?: () => void;
}

export interface NetSuiteList {
  netSuiteField: string;
  listItems: Option[];
}

export default function NetsuitePanel(props: Props) {
  const [mappedFields, setMappedFields] = useState<NetSuiteMap[]>([]);
  const [metadataType, setMetadataType] = useState<string>(props.metadataType);
  const [selectedConfig, setSelectedConfig] = useState<number | undefined>(
    undefined
  );
  const [metadata, setMetadata] = useState<Option[]>([]);
  const [isLoading, setisLoading] = useState(false);
  const [isListLoading, setIsListLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [passedField, setPassedField] = useState<NetSuiteMap>();
  const { items, loading } = useNetSuiteConfigs(props.teamId);
  const [listFields, setListFields] = useState<NetSuiteList[] | null>(null);
  const api = useApi();
  const [expanded, setExpanded] = useState(true);
  const metaDataOptions = getMetaDataOptions(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [recordTypeToDelete, setRecordTypeToDelete] = useState<string | null>(
    null
  );
  const [isDeleteFieldModalOpen, setIsDeleteFieldModalOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<{
    index: number;
    fieldName: string;
  } | null>(null);
  const [isHydratingState, setIsHydratingState] = useState(false);
  const [metadataRefreshCounter, setMetadataRefreshCounter] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [metadataFetchError, setMetadataFetchError] = useState<string | null>(null);
  // Surfaces non-blocking field-name validation warnings from POST Mapping (added 2026-08-26,
  // QA retest site 100050: "no validation on field names... no warning anywhere"). The save still
  // succeeds -- this is informational only.
  const [mappingWarnings, setMappingWarnings] = useState<string[]>([]);

  const applyMappingWarnings = async (resp: Response) => {
    try {
      const body = await resp.clone().json();
      setMappingWarnings(Array.isArray(body?.warnings) ? body.warnings : []);
    } catch (err) {
      // Non-JSON or empty body — the save itself already succeeded, so this is non-fatal;
      // just nothing to surface as a warning banner.
      console.error("Failed to parse mapping-save warnings:", err);
    }
  };

  /**
   * "Sync from NetSuite" — invalidates the server-side field metadata cache
   * (24h Redis) and refetches. Use after admins add custom fields in NetSuite
   * and want them surfaced in the mapper without waiting for cache expiry.
   * Backed by POST /NetSuite/Integrations/FieldDefinitions/Refresh/{id}/{recordType}.
   */
  const syncFieldsFromNetSuite = async () => {
    if (!metadataType || !props.selectedConfigId) return;
    setSyncing(true);
    setSyncError(null);
    try {
      const recordType =
        metadataType === "Ticket" ? "SupportCase"
        : metadataType === "Order" ? "Order"
        : metadataType;
      const resp = await api.messaging.post(
        `NetSuite/Integrations/FieldDefinitions/Refresh/${props.selectedConfigId}/${recordType}`,
        {}
      );
      if (!resp.ok) {
        const body = await resp.text().catch(() => "");
        throw new Error(`HTTP ${resp.status}: ${body.slice(0, 200)}`);
      }
      setLastSyncedAt(new Date());
      setMetadataRefreshCounter((c) => c + 1);
    } catch (e) {
      setSyncError(e instanceof Error ? e.message : String(e));
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (!loading) {
      setSelectedConfig(items[0]?.id);
    }
  }, [items, loading]);

  useEffect(() => {
    async function fetchMetadata() {
      //This is the REST method of getting metadata
      //Currently, NetSuite does not support this for all record types, so we are commenting this out and using restlets for now
      // const resp = await api.messaging.get(
      //   `NetSuite/Integrations/GetMetadata/${metadataType}`
      // );
      //setMetadata(json.properties);

      if (!metadataType) {
        setMetadata([]);
        return;
      }

      // Fixed 2026-08-26 (QA retest, site 100050): this guard used to run AFTER setisLoading(true)
      // below, so a render where selectedConfigId was still null/undefined (e.g. right after
      // "Sync from NetSuite" bumps metadataRefreshCounter and re-runs this effect before the config
      // has resolved) left isLoading permanently true -- the row's <select> stayed on
      // "Loading..." forever until a full page refresh. Bail out BEFORE flipping isLoading on.
      if (props.selectedConfigId == null) {
        setisLoading(false);
        return;
      }

      setisLoading(true);
      let metadataToSend = metadataType;
      if (metadataType === "Ticket") {
        metadataToSend = "supportcase";
      }
      if (metadataType === "Order") {
        metadataToSend = "salesorder";
      }

      // Fixed 2026-08-19 (QA retest, site 100050): field discovery went through RelayToRestlet
      // (relayType: "fields") for every type except Transaction/Products/ReturnAuthorization,
      // which required an admin-configured RESTlet URL -- a red banner ("RestletUrl is not
      // configured...") blocked mapping ANY field on Account/Customer/Estimate/etc even though
      // those types don't actually need a RESTlet. The standard REST metadata-catalog endpoint
      // (NetSuiteFieldMetadataService.GetFieldDefinitionsAsync, exposed as GET
      // NetSuite/Integrations/FieldDefinitions/{configId}/{recordType}) already exists, uses
      // OAuth 2.0 like every other call, and is what syncFieldsFromNetSuite's Refresh POST above
      // already targets -- but nothing here ever fetched from its GET counterpart, so a
      // successful sync had nothing to actually display. Route every type through it now.
      const endpoint = `NetSuite/Integrations/FieldDefinitions/${props.selectedConfigId}/${metadataToSend}`;

      try {
        setMetadataFetchError(null);
        const resp = await api.messaging.get(endpoint);
        if (!resp.ok) {
          const body = await resp.text().catch(() => "");
          setMetadataFetchError(
            `Failed to load ${metadataType} fields from NetSuite. HTTP ${resp.status}: ${body.slice(0, 200)}`
          );
          return;
        }
        const json = await resp.json();
        const fields: { name: string; label?: string }[] = json?.fields ?? json?.Fields ?? [];
        const newOptions = [...fields]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((f) => ({ value: f.name, label: f.label || f.name })) as Option[];

        if (metadataType === "Estimate") {
          const itemFieldExists = newOptions.some(
            (option) => option.value === "item:items:item:id"
          );
          if (!itemFieldExists) {
            newOptions.push({
              value: "item:items:item:id",
              label: "item:items:item:id"
            });
          }
        }

        if (metadataType === "ReturnAuthorization") {
          const memoFieldExists = newOptions.some(
            (option) => option.value === "memo"
          );
          if (!memoFieldExists) {
            newOptions.push({
              value: "memo",
              label: "memo"
            });
          }
        }

        setMetadata(newOptions);
      } catch (err) {
        setMetadataFetchError(
          `NetSuite field fetch failed: ${err instanceof Error ? err.message : String(err)}`
        );
      } finally {
        setisLoading(false);
      }
    }

    fetchMetadata();
    // metadataRefreshCounter deliberately included: syncFieldsFromNetSuite bumps it after a
    // successful cache-invalidating Refresh POST specifically so this effect re-runs and pulls
    // the fresh field list. Without it in the dependency array, clicking "Sync from NetSuite"
    // silently did nothing visible -- the cache refreshed server-side but the dropdown never
    // re-fetched to show it (2026-08-19 QA retest).
  }, [api.messaging, metadataType, props.selectedConfigId, metadataRefreshCounter]);

  const addRow = () => {
    // A new row starts with a blank field name — if a search filter is active it
    // would never match and the row would look like it silently failed to add.
    if (props.filterText) props.onClearFilter?.();
    const tempId = Math.min(...mappedFields.map(f => f.id), 0) - 1;

    const newMap: NetSuiteMap = {
      id: tempId, 
      netSuiteField: {
        value: "",
        label: ""
      },
      label: "",
      linkedTo: "",
      create: false,
      usedForSearching: false,
      showWhenSelected: false,
      showInSearch: false,
      control: "",
      defaultValue: "",
      recordType: metadataType
    };

    setMappedFields([...mappedFields, newMap]);
  };

  const lastSaved = useRef<NetSuiteMap[] | null>(null);
  const saveTimeout = useRef<NodeJS.Timeout>();
  const isInitialRender = useRef(true);
  const isHydrating = useRef(false);

  const handleDeleteFieldClick = (index: number) => {
    const field = mappedFields[index];
    const fieldName = field.netSuiteField?.label || field.label || "this field";
    setFieldToDelete({ index, fieldName });
    setIsDeleteFieldModalOpen(true);
  };

  const confirmDeleteField = () => {
    if (fieldToDelete === null) return;

    const index = fieldToDelete.index;
    if (mappedFields[index].id !== -1) {
      deleteRecord(mappedFields[index].id);
    }
    const clone = [...mappedFields];
    clone.splice(index, 1);
    setMappedFields(clone);
    setIsDeleteFieldModalOpen(false);
    setFieldToDelete(null);
  };

  const isPersonDefaultValue = mappedFields.find(
    f => f.netSuiteField?.value?.toLowerCase() === "isperson"
  )?.defaultValue;

  const requiredFields = useMemo(() => {
    return getRequiredFields(metadataType, isPersonDefaultValue?.toString());
    // metadataRefreshCounter included so the Sync button triggers a refetch.
  }, [metadataType, isPersonDefaultValue, metadataRefreshCounter]);

  const fetchMapping = useCallback(async () => {
    if (props.selectedConfigId == undefined || metadataType == "") return;

    isHydrating.current = true;
    setIsHydratingState(true);
    const resp = await api.messaging.get(
      `NetSuite/Integrations/Mapping/${metadataType}/${props.selectedConfigId}`
    );

    const itemsToBeListed: NetSuiteMap[] = [];
    const json = await resp.json();

    json.forEach((element: any) => {
      const fieldName = typeof element.netSuiteField === 'string'
        ? element.netSuiteField
        : element.netSuiteField?.value;

      if (fieldName?.toLowerCase() === "isperson" || fieldName?.toLowerCase() === "subsidiary") {
        element.control = "List";
      }

      const value = {
        value: element.netSuiteField,
        label: element.netSuiteField
      };
      element.netSuiteField = value;
    });

    // Remove duplicates from json (case-insensitive)
    const uniqueJson = json.filter(
      (field: NetSuiteMap, index: number, self: any[]) => {
        const fieldValue = field.netSuiteField?.value;
        if (!fieldValue) return true; // Keep empty fields
        const fieldValueLower = fieldValue.toLowerCase();
        // Keep only the first occurrence of each field value (case-insensitive)
        return (
          self.findIndex(
            (f: NetSuiteMap) => f.netSuiteField?.value?.toLowerCase() === fieldValueLower
          ) === index
        );
      }
    );

    const persistedFields = uniqueJson.filter(
      (field: NetSuiteMap) => field.id > 0
    );

    const fieldsToAdd: NetSuiteMap[] = [];
    const fieldsToRemove: number[] = [];

    const isPersonField = uniqueJson.find(
      (field: NetSuiteMap) => field.netSuiteField?.value?.toLowerCase() === "isperson"
    );
    const isPersonValue = isPersonField?.defaultValue?.toString();
    const currentRequiredFields = getRequiredFields(metadataType, isPersonValue);
    const currentRequiredFieldsSet = new Set(currentRequiredFields);

    const possibleRequiredFields = [
      "firstname",
      "lastname",
      "companyname",
      "email"
    ];

    uniqueJson.forEach((field: NetSuiteMap) => {
      const fieldValue = field.netSuiteField?.value?.toLowerCase();
      if (
        fieldValue &&
        possibleRequiredFields.includes(fieldValue) &&
        !currentRequiredFieldsSet.has(fieldValue) &&
        field.create === true &&
        !field.label &&
        !field.linkedTo &&
        !field.defaultValue
      ) {
        fieldsToRemove.push(field.id);
      }
    });

    const filteredJson = uniqueJson.filter(
      (field: NetSuiteMap) => !fieldsToRemove.includes(field.id)
    );

    // Create set of existing field values from filteredJson to prevent duplicates
    const existingFieldValuesSet = new Set(
      filteredJson
        .map((field: NetSuiteMap) => field.netSuiteField?.value?.toLowerCase())
        .filter(Boolean)
    );

    currentRequiredFields.forEach((requiredField) => {
      const requiredFieldLower = requiredField.toLowerCase();
      if (!existingFieldValuesSet.has(requiredFieldLower)) {
        const control =
          requiredFieldLower === "isperson" ||
          requiredFieldLower === "subsidiary"
            ? "List"
            : "";

        const tempId = Math.min(...[...filteredJson, ...fieldsToAdd].map(f => f.id), 0) - 1;

        fieldsToAdd.push({
          id: tempId,
          netSuiteField: {
            value: requiredField,
            label: requiredField
          },
          label: "",
          linkedTo: "",
          create: true,
          usedForSearching: false,
          showWhenSelected: false,
          showInSearch: false,
          control: control,
          defaultValue: "",
          recordType: metadataType
        });
        existingFieldValuesSet.add(requiredFieldLower);
      }
    });

    setMappedFields([...filteredJson, ...fieldsToAdd]);

    // Wait for all deletes to complete before adding new fields
    if (fieldsToRemove.length > 0) {
      console.info(`Automatically removing ${fieldsToRemove.length} obsolete required field(s) due to isperson value change`);

      const deletePromises = fieldsToRemove
        .filter(id => id > 0)
        .map(async (id) => {
          try {
            await api.messaging.delete(
              `NetSuite/Integrations/Mapping/${id}`
            );
          } catch (error) {
            console.error("Error deleting field:", id, error);
          }
        });

      await Promise.all(deletePromises);
    }

    if (fieldsToAdd.length > 0) {
      setTimeout(async () => {
        try {
          const newMappings = fieldsToAdd.map((field) => ({
            netSuiteField: field.netSuiteField?.value,
            id: 0,
            label: field.label,
            linkedTo: field.linkedTo,
            create: field.create,
            usedForSearching: field.usedForSearching,
            showWhenSelected: field.showWhenSelected,
            showInSearch: field.showInSearch,
            control: field.control,
            defaultValue: field.defaultValue,
            recordType: field.recordType
          }));

          await api.messaging.post(
            `NetSuite/Integrations/Mapping/${props.selectedConfigId}`,
            {
              Mappings: newMappings
            }
          );

          const fetchResp = await api.messaging.get(
            `NetSuite/Integrations/Mapping/${metadataType}/${props.selectedConfigId}`
          );
          const updatedMappings = await fetchResp.json();

          updatedMappings.forEach((element: any) => {
            const fieldName = typeof element.netSuiteField === 'string'
              ? element.netSuiteField
              : element.netSuiteField?.value;

            if (fieldName?.toLowerCase() === "isperson" || fieldName?.toLowerCase() === "subsidiary") {
              element.control = "List";
            }

            const value = {
              value: element.netSuiteField,
              label: element.netSuiteField
            };
            element.netSuiteField = value;
          });

          lastSaved.current = updatedMappings;
          setMappedFields(updatedMappings);
        } catch (error) {
          console.error("Error inserting required fields:", error);
          setIsHydratingState(false);
        } finally {
          isHydrating.current = false;
          setIsHydratingState(false);
        }
      }, 1000);
    } else {
      isHydrating.current = false;
      setIsHydratingState(false);
    }
  }, [api.messaging, metadataType, props.selectedConfigId]);

  useEffect(() => {
    if (!metadataType) return;
    fetchMapping();
  }, [fetchMapping]);

  function isOption(value: any): value is Option {
    return value && typeof value === "object" && "value" in value;
  }

  const saveRecord = useCallback(async () => {
    const fieldsToSave = mappedFields.filter((f) => f.id > 0);

    if (fieldsToSave.length === 0) {
      return;
    }

    const newMap: {
      netSuiteField: any;
      id: number;
      label: string;
      linkedTo: string;
      create: boolean;
      usedForSearching: boolean;
      showWhenSelected: boolean;
      showInSearch: boolean;
      control: string;
      defaultValue: string | SingleValue<Option>;
      recordType: string;
    }[] = [];
    fieldsToSave.forEach((element) => {
      newMap.push({
        netSuiteField: element.netSuiteField?.value,
        id: element.id,
        label: element.label,
        linkedTo: element.linkedTo,
        create: element.create,
        usedForSearching: element.usedForSearching,
        showWhenSelected: element.showWhenSelected,
        showInSearch: element.showInSearch,
        control: element.control,
        defaultValue:
          element.defaultValue && isOption(element.defaultValue)
            ? element.defaultValue.value
            : element.defaultValue,
        recordType: element.recordType
      });
    });

    const response = await api.messaging.post(
      `NetSuite/Integrations/Mapping/${props.selectedConfigId}`,
      {
        Mappings: newMap
      }
    );
    await applyMappingWarnings(response);
  }, [mappedFields, props.selectedConfigId, api.messaging]);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    if (isHydrating.current) return;

    if (mappedFields.some((f) => f.id < 0)) {
      return;
    }

    if (lastSaved.current === mappedFields) return;

    if (saveTimeout.current) clearTimeout(saveTimeout.current);

    saveTimeout.current = setTimeout(() => {
      saveRecord();
      lastSaved.current = mappedFields;
    }, 300); 
  }, [mappedFields, saveRecord]);

  async function deleteRecord(id: number) {
    const response = await api.messaging.delete(
      `NetSuite/Integrations/Mapping/${id}`
    );
  }

  const handleDeleteRecordClick = (recordType: string) => {
    setRecordTypeToDelete(recordType);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteRecord = async () => {
    if (!recordTypeToDelete) return;

    const response = await api.messaging.delete(
      `NetSuite/Integrations/Mapping/DeleteRecord/${recordTypeToDelete}`
    );

    const updatedFields = mappedFields.filter(
      (field) => field.recordType !== recordTypeToDelete
    );
    setMappedFields(updatedFields);
    setIsDeleteModalOpen(false);
    setRecordTypeToDelete(null);
  };

  const updateMappedField = async (index: number, mappedField: NetSuiteMap) => {
    const clone = [...mappedFields];

    // Check if the selected field already exists in another row. Case-insensitive (2026-08-26
    // fix, QA retest site 100050): a saved mapping could carry different casing than the current
    // dropdown option casing (e.g. a legacy "Email" row vs. a freshly-fetched "email" option) --
    // an exact-match check silently let that dupe through with no warning.
    const fieldValue = mappedField.netSuiteField?.value;
    if (fieldValue) {
      const fieldValueLower = fieldValue.toLowerCase();
      const duplicateIndex = clone.findIndex(
        (field, idx) =>
          idx !== index && field.netSuiteField?.value?.toLowerCase() === fieldValueLower
      );

      if (duplicateIndex !== -1) {
        alert(
          `The field "${mappedField.netSuiteField?.label}" is already mapped. Please select a different field.`
        );
        return;
      }
    }

    // Disable usedForSearching for fields with colons to prevent NetSuite errors
    if (fieldValue && fieldValue.includes(':')) {
      mappedField.usedForSearching = false;
    }

    const isIsPersonField = mappedField.netSuiteField?.value?.toLowerCase() === "isperson";
    const oldIsPersonField = clone[index];
    const isPersonValueChanged =
      isIsPersonField &&
      oldIsPersonField?.defaultValue !== mappedField.defaultValue;

    clone[index] = mappedField;

    if (mappedField.id < 0 && mappedField.netSuiteField?.value) {
      const newMapping = {
        netSuiteField: mappedField.netSuiteField?.value,
        id: 0, 
        label: mappedField.label,
        linkedTo: mappedField.linkedTo,
        create: mappedField.create,
        usedForSearching: mappedField.usedForSearching,
        showWhenSelected: mappedField.showWhenSelected,
        showInSearch: mappedField.showInSearch,
        control: mappedField.control,
        defaultValue: isOption(mappedField.defaultValue)
            ? mappedField.defaultValue.value
            : mappedField.defaultValue,
        recordType: mappedField.recordType
      };

      try {
        const mapResponse = await api.messaging.post(
          `NetSuite/Integrations/Mapping/${props.selectedConfigId}`,
          {
            Mappings: [newMapping]
          }
        );
        await applyMappingWarnings(mapResponse);

        const fetchResp = await api.messaging.get(
          `NetSuite/Integrations/Mapping/${metadataType}/${props.selectedConfigId}`
        );
        const updatedMappings = await fetchResp.json();

        updatedMappings.forEach((element: any) => {
          const fieldName = typeof element.netSuiteField === 'string'
            ? element.netSuiteField
            : element.netSuiteField?.value;

          if (fieldName?.toLowerCase() === "isperson" || fieldName?.toLowerCase() === "subsidiary") {
            element.control = "List";
          }

          const value = {
            value: element.netSuiteField,
            label: element.netSuiteField
          };
          element.netSuiteField = value;
        });

        // Update the state with fresh data from backend
        setMappedFields(updatedMappings);
        lastSaved.current = updatedMappings;
        return;
      } catch (error) {
        console.error("Error inserting new mapping:", error);
      }
    }

    setMappedFields(clone);

    // If isperson value changed, trigger a refresh to update required fields
    if (isPersonValueChanged) {
      setTimeout(async () => {
        await fetchMapping();
      }, 500);
    }
  };

  function handleModal(passedField: NetSuiteMap) {
    setShowModal(true);
    setPassedField(passedField);
  }

  const saveLinkedTo = useCallback(
    (linkedField: NetSuiteMap) => {
      const clone = [...mappedFields];
      clone[linkedField.id] = linkedField;
      setMappedFields(clone);
    },
    [mappedFields]
  );

  // Fixed 2026-08-22 (same Data Mapping QA report as commit 3914f903b's fetchMetadata fix,
  // site 100050): this batch call went through NetSuite/Integrations/RelayToRestlet (relayType:
  // "getList") for every record type except Transaction/Products/ReturnAuthorization/Estimate --
  // same "RestletUrl is not configured" 400 as the field-NAME dropdown had, just for the
  // list-VALUES of a select/multiselect-type field (e.g. populating the dropdown of valid
  // Subsidiary/isperson-style values once a field is set to Input Type "List"). The existing
  // GET NetSuite/Integrations/ListValues/{configId}/{recordType}/{fieldName} endpoint
  // (NetSuiteFieldMetadataService.GetListValuesAsync, fixed earlier the same day this feature
  // was audited to also use the customlist_<suffix> fallback for custom fields) already covers
  // this with no RESTlet needed -- it was just never called from here. One GET per field
  // (fields are typically 1-2 per save, never a large batch) instead of RelayToRestlet's single
  // batched POST; per-field failures are isolated instead of one bad field blanking the batch.
  const fetchList = useCallback(
    async (passedFields: NetSuiteMap[]) => {
      const validFields = passedFields.filter(
        (field) => field.netSuiteField?.value
      );

      if (validFields.length === 0) return;
      if (props.selectedConfigId == null) return;

      const normalizeRecordType = (recordType: string) => {
        if (recordType === "Ticket") return "supportcase";
        if (recordType === "Order") return "salesorder";
        return recordType;
      };

      try {
        const results = await Promise.all(
          validFields.map(async (passedField) => {
            const recordType = normalizeRecordType(passedField.recordType);
            const fieldName = passedField.netSuiteField?.value;
            const resp = await api.messaging.get(
              `NetSuite/Integrations/ListValues/${props.selectedConfigId}/${recordType}/${fieldName}`
            );
            if (!resp.ok) {
              console.error(
                `Failed to fetch list values for ${recordType}.${fieldName}:`,
                resp.status,
                resp.statusText
              );
              return { fieldName, values: null };
            }
            const values: { id: string; name: string }[] = await resp.json();
            return { fieldName, values };
          })
        );

        const clone = [...(listFields || [])];

        results.forEach(({ fieldName, values }) => {
          const existingIndex = clone.findIndex(
            (item) => item.netSuiteField === fieldName
          );
          if (existingIndex > -1) clone.splice(existingIndex, 1);

          if (values && values.length > 0) {
            const listItems = values.map((item) => ({
              value: item.id,
              label: item.name.replace(/&nbsp;/gi, "")
            }));

            clone.push({ netSuiteField: fieldName, listItems });
          }
        });
        setListFields(clone);
      } catch (error) {
        console.error("Error fetching list items:", error);
      }
    },
    [api.messaging, listFields, props.selectedConfigId]
  );

  useEffect(() => {
    if (mappedFields.length === 0 || loading || listFields != null) return;
    const mappedFieldsToBeListed = mappedFields.filter(
      (item) => item.control === "List"
    );
    // fetchList also has a guard, but double-checking here for safety
    if (mappedFieldsToBeListed.length > 0) {
      fetchList(mappedFieldsToBeListed);
    }
  }, [fetchList, listFields, loading, mappedFields]);

  const ready = useMemo(() => !loading, [loading]);
  if (!ready) {
    return <></>;
  }

  const chevronClass = `w-4 h-4 transition-transform${expanded ? " rotate-180" : ""}`;

  return (
    <div className="panel bg-slate-gray-50 rounded-md shadow-md mb-4">
      <div
        className="panel-header flex items-center h-16 px-4 cursor-pointer"
        onClick={() => setExpanded((s) => !s)}
      >
        <div
          className="text-zinc-50 dark:text-white font-medium text-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <CustomSelect
            options={metaDataOptions}
            value={metadataType}
            width="w-72"
            onChange={setMetadataType}
          />
        </div>

        <div className="ml-auto flex items-center gap-3 dark:text-white">
          {metadataType && props.selectedConfigId ? (
            <div
              className="flex items-center gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              {lastSyncedAt && (
                <span className="text-xs text-zinc-300">
                  Synced {lastSyncedAt.toLocaleTimeString()}
                </span>
              )}
              {syncError && (
                <span className="text-xs text-rose-300" title={syncError}>
                  Sync failed
                </span>
              )}
              <button
                type="button"
                onClick={syncFieldsFromNetSuite}
                disabled={syncing}
                className="text-xs px-2 py-1 rounded border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 disabled:opacity-60 disabled:cursor-not-allowed"
                title="Force a fresh pull of field definitions from NetSuite (bypasses the 24h cache)"
              >
                {syncing ? "Syncing…" : "Sync from NetSuite"}
              </button>
            </div>
          ) : null}
          <button
            className="h-8 w-8 text-zinc-400"
            onClick={() => setExpanded((s) => !s)}
          >
            <ChevronDown className={chevronClass} />
          </button>
        </div>
      </div>
      {metadataFetchError && (
        <div className="mx-4 mt-2 mb-0 p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-800">
          {metadataFetchError}
        </div>
      )}
      {mappingWarnings.length > 0 && (
        <div className="mx-4 mt-2 mb-0 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
          {mappingWarnings.map((w, i) => (
            <p key={i}>{w}</p>
          ))}
        </div>
      )}
      {expanded && (
        <div className="panel-body rounded-2xl animated fadeIn dark:border-stone-100 p-4">
          <p className="mb-4 text-xs text-zinc-500 leading-relaxed border-l-2 border-zinc-300 pl-3">
            Field mappings tell Velaro how to recognize and display customers from NetSuite.
            Mark at least two <strong className="text-zinc-700">Used For Searching</strong> fields — for example Email and Phone — so contacts who share one value can still be matched reliably during bot conversations and agent searches.
            <strong className="text-zinc-700"> Show In Search</strong> fields appear when multiple matches are found (both in bot disambiguation and agent search).
            <strong className="text-zinc-700"> Show When Selected</strong> fields populate the agent sidebar once a contact is confirmed.
            <strong className="text-zinc-700"> Create</strong> fields sync data the bot collects back to NetSuite.
          </p>
          <table className="p-4 w-full bg-white">
            <thead>
              <tr>
                <th className="pb-4 p-2 pl-4 w-80 text-center">
                  Netsuite Field
                </th>
                <th className="pb-4 p-2 w-80 text-center">
                  Label
                  <ColumnHelp description="The display name agents see in the sidebar instead of the raw NetSuite field name" />
                </th>
                <th className="pb-4 p-2 w-80 text-center">Input Type</th>
                <th className="pb-4 p-2 w-80 text-center">Default Value</th>
                <th className="pb-4 p-2 w-24 text-center">
                  Create
                  <ColumnHelp description="When bot collects this value, write it back to this NetSuite field" />
                </th>
                <th className="pb-4 p-2 w-24 text-center">
                  Used For Searching
                  <ColumnHelp description="Use this field as a key when searching for NetSuite records" />
                </th>
                <th className="pb-4 p-2 w-24 text-center">
                  Show When Selected
                  <ColumnHelp description="Show this field in the agent info panel when a customer record is matched" />
                </th>
                <th className="pb-4 p-2 w-24 text-center">
                  Show In Search
                  <ColumnHelp description="Show this field when multiple matches are found, in both bot disambiguation and agent search" />
                </th>
                <th className="pb-4 p-2 w-24 text-center"></th>
              </tr>
            </thead>
            <tbody>
              {mappedFields
                .map((mappedField, i) => ({ mappedField, i }))
                .filter(
                  ({ mappedField }) =>
                    !props.filterText ||
                    (mappedField.netSuiteField?.label || "")
                      .toLowerCase()
                      .includes(props.filterText.toLowerCase())
                )
                .map(({ mappedField, i }) => (
                  <NetSuitePanelRow
                    mappedField={mappedField}
                    i={i}
                    key={i}
                    listFields={listFields || []}
                    updateMappedField={updateMappedField}
                    isLoading={isLoading}
                    deleteRow={handleDeleteFieldClick}
                    handleModal={handleModal}
                    metadata={metadata}
                    fetchList={fetchList}
                    saveRecord={saveRecord}
                    showSavedMessage={props.showSavedMessage}
                    metadataType={metadataType}
                    requiredFields={requiredFields}
                    isHydrating={isHydratingState}
                  />
                ))}
              {mappedFields.length > 0 &&
                props.filterText &&
                !mappedFields.some((f) =>
                  (f.netSuiteField?.label || "")
                    .toLowerCase()
                    .includes(props.filterText!.toLowerCase())
                ) && (
                  <tr>
                    <td colSpan={9} className="py-6 text-center text-xs text-zinc-400 italic">
                      No fields match "{props.filterText}".
                    </td>
                  </tr>
                )}
            </tbody>
          </table>

          {metadataType && (
            <div className="flex justify-end pt-4 gap-3">
              <SecondaryButton
                onClick={() => handleDeleteRecordClick(metadataType)}
                disabled={props.isDeleteDisabled || mappedFields.length === 0}
              >
                Delete record
              </SecondaryButton>
              <SecondaryButton onClick={addRow}>Add field</SecondaryButton>
            </div>
          )}

          {showModal && passedField && (
            <NetSuiteModal
              netSuiteMap={passedField}
              onClose={() => setShowModal(false)}
              saveLinkedField={() => saveLinkedTo}
            />
          )}
        </div>
      )}

      {isDeleteModalOpen && (
        <Modal show={isDeleteModalOpen}>
          <ModalBody>
            <div className="flex items-center mb-6 justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-8 text-[#EE3763]" />
                <div className="text-zinc-800 text-xl font-medium">
                  Delete Record Type
                </div>
              </div>
              <button onClick={() => setIsDeleteModalOpen(false)}>
                <IconClose className="w-8 text-zinc-500" />
              </button>
            </div>

            <div className="text-center text-zinc-800 text-sm font-normal">
              Are you sure you want to delete all field mappings for{" "}
              <strong>{recordTypeToDelete}</strong>? This action cannot be
              undone and will remove all mapped fields for this record type.
            </div>
            <div className="pt-6 flex justify-center gap-4">
              <GhostButton onClick={() => setIsDeleteModalOpen(false)}>Cancel</GhostButton>
              <PrimaryButton onClick={confirmDeleteRecord}>Delete</PrimaryButton>
            </div>
          </ModalBody>
        </Modal>
      )}

      {isDeleteFieldModalOpen && (
        <Modal show={isDeleteFieldModalOpen}>
          <ModalBody>
            <div className="flex items-center mb-6 justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-8 text-[#EE3763]" />
                <div className="text-zinc-800 text-xl font-medium">
                  Delete Field Mapping
                </div>
              </div>
              <button onClick={() => setIsDeleteFieldModalOpen(false)}>
                <IconClose className="w-8 text-zinc-500" />
              </button>
            </div>

            <div className="text-center text-zinc-800 text-sm font-normal">
              Are you sure you want to delete the field mapping for{" "}
              <strong>{fieldToDelete?.fieldName}</strong>? This action cannot be
              undone.
            </div>
            <div className="pt-6 flex justify-center gap-4">
              <GhostButton onClick={() => setIsDeleteFieldModalOpen(false)}>Cancel</GhostButton>
              <PrimaryButton onClick={confirmDeleteField}>Delete</PrimaryButton>
            </div>
          </ModalBody>
        </Modal>
      )}
    </div>
  );
}
